import { useState } from "react";
import { Icon } from "../icons/icon";
import { AddFieldButton } from "./AddFieldButton";
import { FieldDisplay } from "./FieldDisplay";
import { InlineInputControl } from "./InlineInputControl";
import { FIELD_CONFIG, type FieldType } from "./types";

interface EditableInputProps {
  /** Field type determines icon and href behavior. Default: "text" */
  type?: FieldType;
  value: string | null | undefined;
  /** Called when user commits a change. Resolve = exit edit mode, reject = stay in edit mode. */
  onSave: (value: string | null) => Promise<void>;
  /** Called when user modifies input (for clearing errors, live validation, etc.) */
  onChange?: (value: string | null) => void;
  placeholder?: string;
  /** Label for the "add" button when value is empty. If not provided, empty state shows nothing. */
  addLabel?: string;
  canEdit?: boolean;
  /** Error message to display while editing. Controlled by parent. */
  error?: string;
  /** Loading state while saving. Controlled by parent. */
  saving?: boolean;
  /** Show icon prefix. Default: true */
  showIcon?: boolean;
  /** Require non-empty value. Default: false */
  required?: boolean;
  /** Custom class for display text (non-editing state) */
  className?: string;
}

export function EditableInput({
  type = "text",
  value,
  onSave,
  onChange,
  placeholder,
  addLabel,
  canEdit,
  error,
  saving = false,
  showIcon = true,
  required = false,
  className,
}: EditableInputProps) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [localError, setLocalError] = useState<string>();

  const config = FIELD_CONFIG[type];
  const icon = showIcon ? <Icon i={config.icon} className="size-4" /> : null;

  const startEditing = () => {
    setEditing(true);
    setEditValue(value ?? "");
    setLocalError(undefined);
  };

  const cancelEditing = () => {
    setEditing(false);
    setEditValue("");
    setLocalError(undefined);
  };

  const handleChange = (newValue: string) => {
    setEditValue(newValue);
    setLocalError(undefined);

    // For link type, auto-prefix https:// if no protocol
    let finalValue: string | null = newValue.trim() || null;
    if (type === "link" && finalValue && !/^https?:\/\//i.test(finalValue)) {
      finalValue = `https://${finalValue}`;
    }
    onChange?.(finalValue);
  };

  const save = async () => {
    const trimmed = editValue.trim();

    // Local required validation (immediate feedback)
    if (required && !trimmed) {
      setLocalError("This field is required");
      return;
    }

    // No change - just exit
    if (trimmed === (value ?? "")) {
      cancelEditing();
      return;
    }

    // For link type, auto-prefix https:// if no protocol
    let finalValue: string | null = trimmed || null;
    if (type === "link" && finalValue && !/^https?:\/\//i.test(finalValue)) {
      finalValue = `https://${finalValue}`;
    }

    try {
      await onSave(finalValue);
      cancelEditing();
    } catch {
      // Stay in edit mode - parent controls error display via prop
    }
  };

  if (editing) {
    return (
      <InlineInputControl
        value={editValue}
        onChange={handleChange}
        onSave={save}
        onCancel={cancelEditing}
        placeholder={placeholder}
        disabled={saving}
        error={localError || error}
        icon={icon}
      />
    );
  }

  if (value) {
    const href = config.getHref?.(value);
    return (
      <FieldDisplay
        icon={icon}
        href={href}
        target={config.target}
        canEdit={canEdit}
        onEdit={startEditing}
        className={className}
      >
        {value}
      </FieldDisplay>
    );
  }

  if (canEdit && addLabel) {
    return <AddFieldButton icon={icon} label={addLabel} onClick={startEditing} />;
  }

  return null;
}
